import { useMemo, useState } from 'react';
import type { TFunction } from 'i18next';
import TextButton from '@/components/@IntranetLibrary/Button/TextButton';
import ApprovalFormTable from '@/components/Approval/@components/ApprovalFormTable/ApprovalFormTable';
import {
  formatBusinessNumber,
  formatExternalLoanAmount as fmt,
  formatIssueDateLong,
  formatMaturityDate,
  formatMaturityShort,
  formatMaturityWithWeekday,
  summarizeExternalLoan,
  type ExternalLoanBank,
  type ExternalLoanItem,
} from '@/utils/approval/externalLoan';
import {
  BankCard,
  BankHeader,
  BankHeaderMeta,
  BankPanel,
  CardAmount,
  CardLabel,
  CardRow,
  ExternalLoanRoot,
  ExternalLoanSection,
  ExternalLoanTitle,
} from './styles';

/** 만기일 묶음마다 먼저 보여 줄 행 수 */
const GROUP_PREVIEW_ROWS = 3;

const NO_ESCAPE = { interpolation: { escapeValue: false } };

type ExternalLoanBodyProps = {
  /** YYYY-MM-DD — 조회한 구매카드 발급일 */
  issueDate: string;
  items: ExternalLoanItem[];
  t: TFunction;
  lang: string;
};

const BankDetail = ({
  bank,
  open,
  onToggle,
  t,
  lang,
}: {
  bank: ExternalLoanBank;
  open: boolean;
  onToggle: () => void;
  t: TFunction;
  lang: string;
}) => {
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});
  return (
    <BankPanel>
      <BankHeader>
        <strong>{bank.bankName}</strong>
        <BankHeaderMeta>
          {t('{{count}}건 · {{amount}}원', {
            count: bank.count,
            amount: fmt(bank.total),
          })}
        </BankHeaderMeta>
        <TextButton
          type="button"
          variant="keyColor"
          size="small"
          aria-expanded={open}
          onClick={onToggle}
        >
          {open ? t('접기') : t('펼치기')}
        </TextButton>
      </BankHeader>
      {open && (
        <ApprovalFormTable className="bank-detail-table">
          <colgroup>
            <col className="col-no" />
            <col className="col-business-number" />
            <col />
            <col className="col-amount" />
            <col className="col-date" />
          </colgroup>
          <thead>
            <tr>
              <th>NO</th>
              <th>{t('사업자번호')}</th>
              <th>{t('거래처명')}</th>
              <th>{t('금액 (원)')}</th>
              <th>{t('만기일')}</th>
            </tr>
          </thead>
          <tbody>
            {bank.groups.map((group) => {
              const more = group.rows.length - GROUP_PREVIEW_ROWS;
              const showAll = expanded[group.maturityDate] || more <= 0;
              const rows = showAll
                ? group.rows
                : group.rows.slice(0, GROUP_PREVIEW_ROWS);
              return [
                ...rows.map((row) => (
                  <tr key={`${group.maturityDate}-${row.no}`}>
                    <td className="align-center">{row.no}</td>
                    <td className="align-center">
                      {formatBusinessNumber(row.businessNumber)}
                    </td>
                    <td className="align-left">{row.vendorName}</td>
                    <td className="align-right">{fmt(row.amount)}</td>
                    <td className="align-center">
                      {formatMaturityDate(row.maturityDate)}
                    </td>
                  </tr>
                )),
                !showAll && (
                  <tr key={`${group.maturityDate}-more`} className="row-more">
                    <td colSpan={5} className="align-left">
                      <TextButton
                        type="button"
                        variant="keyColor"
                        size="small"
                        onClick={() =>
                          setExpanded((prev) => ({
                            ...prev,
                            [group.maturityDate]: true,
                          }))
                        }
                      >
                        {t('{{count}}건 더 보기', { count: more })}
                      </TextButton>
                    </td>
                  </tr>
                ),
                <tr
                  key={`${group.maturityDate}-subtotal`}
                  className="row-subtotal"
                >
                  <td colSpan={3} className="align-left">
                    {t('{{date}} 만기 소계', {
                      date: formatMaturityWithWeekday(group.maturityDate, lang),
                    })}
                  </td>
                  <td className="align-right">{fmt(group.total)}</td>
                  <td className="align-center">
                    {t('{{count}}건', { count: group.rows.length })}
                  </td>
                </tr>,
              ];
            })}
          </tbody>
        </ApprovalFormTable>
      )}
    </BankPanel>
  );
};

/**
 * 외담대 발행(217) 본문 영역 1~3 — 작성·조회 화면이 같이 씀.
 * 금액은 SAP 거래처별 집계 그대로, 은행 합계·만기일 소계·총합계는 여기서 합산
 */
const ExternalLoanBody = ({
  issueDate,
  items,
  t,
  lang,
}: ExternalLoanBodyProps) => {
  const summary = useMemo(() => summarizeExternalLoan(items), [items]);
  const [openBanks, setOpenBanks] = useState<Record<string, boolean>>(() =>
    summary.banks[0] ? { [summary.banks[0].bankName]: true } : {},
  );
  const tableMinWidth = 240 + summary.maturityDates.length * 160 + 180;

  return (
    <ExternalLoanRoot className="external-loan">
      <ExternalLoanSection>
        <ExternalLoanTitle>
          {t('1. 외담대 발행 (구매카드 발급일 {{date}})', {
            date: formatIssueDateLong(issueDate, lang),
          })}
        </ExternalLoanTitle>
        <CardRow>
          <BankCard className="is-total">
            <CardLabel>
              {t('총 발행액 · {{count}}건', { count: summary.count })}
            </CardLabel>
            <CardAmount>
              {t('{{amount}}원', { amount: fmt(summary.total) })}
            </CardAmount>
          </BankCard>
          {summary.banks.map((bank) => (
            <BankCard key={bank.bankName}>
              <CardLabel>
                {t('{{bank}} · {{count}}건', {
                  bank: bank.bankName,
                  count: bank.count,
                  ...NO_ESCAPE,
                })}
              </CardLabel>
              <CardAmount>
                {t('{{amount}}원', { amount: fmt(bank.total) })}
              </CardAmount>
            </BankCard>
          ))}
        </CardRow>
      </ExternalLoanSection>

      <ExternalLoanSection>
        <ExternalLoanTitle>{t('2. 은행 · 만기일별 소계')}</ExternalLoanTitle>
        <ApprovalFormTable
          className="maturity-table"
          style={{ minWidth: tableMinWidth }}
        >
          <thead>
            <tr>
              <th className="col-bank">{t('지급은행')}</th>
              <th>{t('건수')}</th>
              {summary.maturityDates.map((date) => (
                <th key={date}>
                  {t('{{date}} 만기', {
                    date: formatMaturityShort(date, lang),
                  })}
                </th>
              ))}
              <th>{t('합계 (원)')}</th>
            </tr>
          </thead>
          <tbody>
            {summary.banks.map((bank) => (
              <tr key={bank.bankName}>
                <td className="col-bank align-left">{bank.bankName}</td>
                <td className="align-right">
                  {t('{{count}}건', { count: bank.count })}
                </td>
                {summary.maturityDates.map((date) => (
                  <td key={date} className="align-right">
                    {date in bank.byMaturity ? fmt(bank.byMaturity[date]) : '-'}
                  </td>
                ))}
                <td className="align-right">
                  <strong>{fmt(bank.total)}</strong>
                </td>
              </tr>
            ))}
            <tr className="row-total">
              <td className="col-bank align-left">{t('합계')}</td>
              <td className="align-right">
                {t('{{count}}건', { count: summary.count })}
              </td>
              {summary.maturityDates.map((date) => (
                <td key={date} className="align-right">
                  {fmt(summary.byMaturity[date])}
                </td>
              ))}
              <td className="align-right">{fmt(summary.total)}</td>
            </tr>
          </tbody>
        </ApprovalFormTable>
      </ExternalLoanSection>

      <ExternalLoanSection>
        <ExternalLoanTitle>{t('3. 은행별 발행내역')}</ExternalLoanTitle>
        {summary.banks.map((bank) => (
          <BankDetail
            key={bank.bankName}
            bank={bank}
            open={!!openBanks[bank.bankName]}
            onToggle={() =>
              setOpenBanks((prev) => ({
                ...prev,
                [bank.bankName]: !prev[bank.bankName],
              }))
            }
            t={t}
            lang={lang}
          />
        ))}
      </ExternalLoanSection>
    </ExternalLoanRoot>
  );
};

export default ExternalLoanBody;
